/**
 * Judged-play HUD numbers, maintained incrementally (W1-06; R§7, F§11.6, A§7.5).
 *
 * Definition (a pure function of t and the JudgeView statuses):
 *   every real note with a status commits at its `at`; a hold that is not a miss commits at max(at, its end);
 *   the commits with time <= t, taken in (commit time, note index) order, give
 *   combo     = consecutive Perfect/Good commits at the end of that sequence, maxCombo = the longest such run,
 *   score     = round(900000 (P + 0.65 G) / N + 100000 maxCombo / N)   (the Phigros scoring Phira uses)
 *   accuracy  = (P + 0.65 G) / commits (1 before the first commit), progress = commits / N.
 *
 * Evaluating that from scratch costs one `JudgeView.status` per real note plus a sort on every frame. The tracker
 * below keeps, per engine and JudgeView, the last polled status of every note and the commits in a list sorted by
 * (commit time, note index) with prefix counters (perfect, good, combo, maxCombo) per position, so a frame costs:
 *   - polling the WATCHED notes: notes without a final status whose time lies within POLL_WINDOW of t (the Bad
 *     window is 0.22 s, F§11.6, so a note is judged well inside it at any playback rate the editor offers) and
 *     holds still being held;
 *   - polling a rolling slice of n / SWEEP_FRAMES other notes, so EVERY note is polled again at least once every
 *     SWEEP_FRAMES frames: a status that changes outside the assumptions above (a judgement far from the note time,
 *     a final status edited later) is picked up within SWEEP_FRAMES frames, without a periodic O(n) spike;
 *   - re-sorting nothing: a changed status moves one entry (binary search) and invalidates the prefix counters from
 *     that position on; the counters are recomputed lazily from the first dirty position (normally the tail);
 *   - one binary search for the commits with time <= t.
 * When t moves backwards (restart, seek) or a slice finds many changes, every note is polled at once and bulk
 * changes (a restart clearing every status) rebuild the list with one sort. A new JudgeView object or a rebuilt
 * note index starts a new tracker.
 */

import type { NoteId } from '../../model/types';
import type { EngineCompileContext, JudgeGrade, JudgeView } from '../types';
import { compareKey } from './sortKeys';
import { cacheOf, globalOf, realNoteIndex, syncedRt, type RealNoteIndex } from './store';

/** Seconds around a note's time during which an unjudged note is polled on every frame. */
export const POLL_WINDOW = 2;
/** Every note is polled at least once per this many frames (rolling slice). */
export const SWEEP_FRAMES = 30;
/** Above this many changed statuses at once the sorted commit list is rebuilt with one sort. */
const BULK_CHANGES = 64;

const GRADE_NONE = 0;
const GRADE_PERFECT = 1;
const GRADE_GOOD = 2;
const GRADE_BAD = 3;
const GRADE_MISS = 4;

function gradeCode(g: JudgeGrade): number {
  switch (g) {
    case 'perfect':
      return GRADE_PERFECT;
    case 'good':
      return GRADE_GOOD;
    case 'bad':
      return GRADE_BAD;
    default:
      return GRADE_MISS;
  }
}

/** Counters of the judged-play HUD (before the score formula). */
export interface JudgedCounts {
  readonly perfect: number;
  readonly good: number;
  readonly combo: number;
  readonly maxCombo: number;
  /** Commits with time <= t. */
  readonly committed: number;
  /** Real notes of the chart (N). */
  readonly total: number;
}

type Status = ReturnType<JudgeView['status']>;

class JudgedTracker {
  readonly judge: JudgeView;
  readonly idx: RealNoteIndex;
  readonly n: number;
  /** End time of holds (NaN for other notes), by index of `idx`. */
  readonly holdEnd: Float64Array;
  /** Commit time of the last polled status (NaN = no status, or a status that never commits). */
  readonly at: Float64Array;
  readonly grade: Uint8Array;
  /** 1 when the last polled status exists and is not `holding`. */
  readonly final: Uint8Array;
  readonly watched: Uint8Array;
  /** Full-poll scratch. */
  readonly nextAt: Float64Array;
  readonly nextGrade: Uint8Array;
  watch: number[] = [];
  /** Notes [0, cursor) have entered the poll window (time <= t + POLL_WINDOW). */
  cursor = 0;
  lastT = Number.NaN;
  /** Next note of the rolling slice. */
  slicePos = 0;
  /** Note indices with a commit time, sorted by (commit time, index). */
  order: number[] = [];
  /** Prefix counters after order[p]. */
  readonly pPerfect: number[] = [];
  readonly pGood: number[] = [];
  readonly pCombo: number[] = [];
  readonly pMax: number[] = [];
  /** First order position whose prefix counters are stale. */
  dirtyFrom = 0;

  constructor(ctx: EngineCompileContext, judge: JudgeView, idx: RealNoteIndex) {
    this.judge = judge;
    this.idx = idx;
    const n = idx.n;
    this.n = n;
    this.holdEnd = new Float64Array(n).fill(Number.NaN);
    const g = globalOf(ctx);
    for (let k = 0; k < n; k++) {
      const id = (idx.id[k] ?? -1) as NoteId;
      const line = g.noteLine.get(id);
      if (line === undefined) continue;
      const rt = syncedRt(ctx, line);
      const i = rt.indexOf.get(id);
      if (i !== undefined && rt.isHold(i)) this.holdEnd[k] = rt.endTime[i] ?? Number.NaN;
    }
    this.at = new Float64Array(n).fill(Number.NaN);
    this.grade = new Uint8Array(n);
    this.final = new Uint8Array(n);
    this.watched = new Uint8Array(n);
    this.nextAt = new Float64Array(n);
    this.nextGrade = new Uint8Array(n);
  }

  /** Commit time and grade code of a status for note k. */
  private commitOf(k: number, s: Status): number {
    if (s === null) return Number.NaN;
    const end = this.holdEnd[k] ?? Number.NaN;
    return s.grade !== 'miss' && !Number.isNaN(end) ? Math.max(s.at, end) : s.at;
  }

  /** Order position of the entry (at, k) (or where it would be inserted). */
  private position(at: number, k: number): number {
    const order = this.order;
    let lo = 0;
    let hi = order.length;
    while (lo < hi) {
      const mid = (lo + hi) >>> 1;
      const m = order[mid] ?? 0;
      const c = compareKey(this.at[m] ?? Number.NaN, at) || m - k;
      if (c < 0) lo = mid + 1;
      else hi = mid;
    }
    return lo;
  }

  /** Moves note k to its new commit (at, grade): one removal and one insertion in the sorted list. */
  private setCommit(k: number, at: number, grade: number): boolean {
    const oldAt = this.at[k] ?? Number.NaN;
    if (Object.is(oldAt, at) && this.grade[k] === grade) return false;
    if (!Number.isNaN(oldAt)) {
      const p = this.position(oldAt, k);
      if (this.order[p] === k) {
        this.order.splice(p, 1);
        if (p < this.dirtyFrom) this.dirtyFrom = p;
      }
    }
    this.at[k] = at;
    this.grade[k] = grade;
    if (!Number.isNaN(at)) {
      const p = this.position(at, k);
      this.order.splice(p, 0, k);
      if (p < this.dirtyFrom) this.dirtyFrom = p;
    }
    return true;
  }

  /** Polls one note; returns whether its status changed. */
  private pollOne(k: number): boolean {
    const s = this.judge.status((this.idx.id[k] ?? -1) as NoteId);
    this.final[k] = s !== null && !s.holding ? 1 : 0;
    return this.setCommit(k, this.commitOf(k, s), s === null ? GRADE_NONE : gradeCode(s.grade));
  }

  private keepWatched(k: number, t: number): boolean {
    if (this.final[k] === 1) return false;
    if (this.grade[k] !== GRADE_NONE) return true;
    return k < this.cursor && (this.idx.time[k] ?? Number.NaN) >= t - POLL_WINDOW;
  }

  private admit(t: number): void {
    const time = this.idx.time;
    while (this.cursor < this.n && (time[this.cursor] ?? Number.NaN) <= t + POLL_WINDOW) {
      const k = this.cursor++;
      if (this.watched[k] === 0 && this.final[k] === 0) {
        this.watched[k] = 1;
        this.watch.push(k);
      }
    }
  }

  /** Polls every note (bulk changes rebuild the sorted list) and rebuilds the watch list. */
  private sweep(t: number): void {
    let changes = 0;
    for (let k = 0; k < this.n; k++) {
      const s = this.judge.status((this.idx.id[k] ?? -1) as NoteId);
      const at = this.commitOf(k, s);
      const grade = s === null ? GRADE_NONE : gradeCode(s.grade);
      this.nextAt[k] = at;
      this.nextGrade[k] = grade;
      this.final[k] = s !== null && !s.holding ? 1 : 0;
      if (!Object.is(at, this.at[k]) || grade !== this.grade[k]) changes++;
    }
    if (changes > BULK_CHANGES) {
      this.at.set(this.nextAt);
      this.grade.set(this.nextGrade);
      const order: number[] = [];
      for (let k = 0; k < this.n; k++) if (!Number.isNaN(this.at[k] ?? Number.NaN)) order.push(k);
      order.sort((a, b) => compareKey(this.at[a] ?? 0, this.at[b] ?? 0) || a - b);
      this.order = order;
      this.dirtyFrom = 0;
    } else if (changes > 0) {
      for (let k = 0; k < this.n; k++) this.setCommit(k, this.nextAt[k] ?? Number.NaN, this.nextGrade[k] ?? 0);
    }
    this.watch = [];
    this.watched.fill(0);
    for (let k = 0; k < this.n; k++) {
      if (this.keepWatched(k, t)) {
        this.watched[k] = 1;
        this.watch.push(k);
      }
    }
  }

  /** Brings the polled statuses up to frame t. */
  advance(t: number): void {
    const backwards = Number.isNaN(this.lastT) || t < this.lastT;
    this.lastT = t;
    this.admit(t);
    if (backwards) {
      this.sweep(t);
      return;
    }
    const watch = this.watch;
    let w = 0;
    for (const k of watch) {
      this.pollOne(k);
      if (this.keepWatched(k, t)) watch[w++] = k;
      else this.watched[k] = 0;
    }
    watch.length = w;
    // Rolling slice of the other notes.
    const n = this.n;
    const step = Math.min(n, Math.ceil(n / SWEEP_FRAMES));
    let changes = 0;
    for (let q = 0; q < step; q++) {
      const k = this.slicePos;
      this.slicePos = k + 1 < n ? k + 1 : 0;
      if (this.watched[k] === 1) continue;
      if (this.pollOne(k)) changes++;
      if (this.keepWatched(k, t)) {
        this.watched[k] = 1;
        watch.push(k);
      }
      if (changes > BULK_CHANGES) {
        this.sweep(t);
        return;
      }
    }
  }

  /** Recomputes stale prefix counters (from the first changed position to the end). */
  private refreshCounters(): void {
    const order = this.order;
    const len = order.length;
    const from = Math.min(this.dirtyFrom, len);
    this.pPerfect.length = len;
    this.pGood.length = len;
    this.pCombo.length = len;
    this.pMax.length = len;
    let perfect = from > 0 ? (this.pPerfect[from - 1] ?? 0) : 0;
    let good = from > 0 ? (this.pGood[from - 1] ?? 0) : 0;
    let combo = from > 0 ? (this.pCombo[from - 1] ?? 0) : 0;
    let max = from > 0 ? (this.pMax[from - 1] ?? 0) : 0;
    for (let p = from; p < len; p++) {
      const g = this.grade[order[p] ?? 0];
      if (g === GRADE_PERFECT || g === GRADE_GOOD) {
        if (g === GRADE_PERFECT) perfect++;
        else good++;
        combo++;
        if (combo > max) max = combo;
      } else combo = 0;
      this.pPerfect[p] = perfect;
      this.pGood[p] = good;
      this.pCombo[p] = combo;
      this.pMax[p] = max;
    }
    this.dirtyFrom = len;
  }

  /** Counters of the commits with time <= t. */
  countsAt(t: number): JudgedCounts {
    this.refreshCounters();
    const order = this.order;
    let lo = 0;
    let hi = order.length;
    while (lo < hi) {
      const mid = (lo + hi) >>> 1;
      if ((this.at[order[mid] ?? 0] ?? Number.NaN) <= t) lo = mid + 1;
      else hi = mid;
    }
    const total = this.idx.commit.length;
    if (lo === 0) return { perfect: 0, good: 0, combo: 0, maxCombo: 0, committed: 0, total };
    const p = lo - 1;
    return {
      perfect: this.pPerfect[p] ?? 0,
      good: this.pGood[p] ?? 0,
      combo: this.pCombo[p] ?? 0,
      maxCombo: this.pMax[p] ?? 0,
      committed: lo,
      total,
    };
  }
}

interface TrackerSlot {
  tracker: JudgedTracker | null;
}

/** Judged-play counters at t for `judge` (incremental; see the file header). */
export function judgedCounts(ctx: EngineCompileContext, t: number, judge: JudgeView): JudgedCounts {
  const idx = realNoteIndex(ctx);
  const slot = cacheOf<TrackerSlot>(globalOf(ctx), 'judgedHud', () => ({ tracker: null }));
  let tr = slot.tracker;
  if (tr?.judge !== judge || tr.idx !== idx) {
    tr = new JudgedTracker(ctx, judge, idx);
    slot.tracker = tr;
  }
  tr.advance(t);
  return tr.countsAt(t);
}
